import React, { useState, useEffect, useMemo } from 'react'
import Header from '@/components/Header'
import { API } from '@/lib/api'
import { httpRequest } from '@/lib/http'
import { toast } from 'sonner'
import {
  Users,
  FileSearch,
  TrendingUp,
  Cpu,
  ShoppingCart,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Zap,
  DollarSign,
  BarChart3,
  Globe,
} from 'lucide-react'
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'

const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

const formatCurrency = (val) => {
  if (val == null) return '-'
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val)
}

const formatNumber = (val) => {
  if (val == null) return '-'
  return new Intl.NumberFormat('fr-FR').format(val)
}

function KpiCard({ icon: Icon, label, value, sub, color = 'text-[#f5f5f7]', trend }) {
  return (
    <div className="bg-[#1c1c1e] border border-white/[0.08] rounded-2xl p-5 flex flex-col gap-1 transition-transform duration-[180ms] hover:scale-[1.01]">
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-xl bg-white/[0.04] flex items-center justify-center">
          <Icon size={18} className="text-[#f5f5f7]/40" />
        </div>
        {trend !== undefined && (
          <span className={`flex items-center gap-0.5 text-xs font-semibold ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#f5f5f7]/40 mt-3">{label}</p>
      <p className={`text-2xl font-black font-heading ${color}`}>{value}</p>
      {sub && <p className="text-xs text-[#f5f5f7]/30 mt-0.5">{sub}</p>}
    </div>
  )
}

function ChartCard({ title, children, className = '' }) {
  return (
    <div className={`bg-[#1c1c1e] border border-white/[0.08] rounded-2xl p-6 ${className}`}>
      <h3 className="text-sm font-bold font-heading text-[#f5f5f7]/60 uppercase tracking-[0.1em] mb-4">{title}</h3>
      {children}
    </div>
  )
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  return (
    <div className="bg-[#2c2c2e] border border-white/[0.08] rounded-xl px-4 py-3 shadow-2xl">
      <p className="text-xs font-semibold text-[#f5f5f7]/50 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-sm font-medium" style={{ color: entry.color }}>
          {entry.name}: {formatNumber(entry.value)}
        </p>
      ))}
    </div>
  )
}

export default function KpiPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const loadKpi = async () => {
    setLoading(true)
    try {
      const kpi = await httpRequest(`${API}/admin/kpi`)
      setData(kpi)
    } catch (err) {
      console.error(err)
      toast.error('Erreur de chargement des KPI')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadKpi() }, [])

  const userPieData = useMemo(() => {
    if (!data) return []
    return [
      { name: 'Google', value: data.users.google },
      { name: 'Email', value: data.users.email },
    ].filter(d => d.value > 0)
  }, [data])

  const analysisPieData = useMemo(() => {
    if (!data) return []
    return [
      { name: 'Anonymes', value: data.analyses.anonymous },
      { name: 'Connectes', value: data.analyses.authenticated },
    ].filter(d => d.value > 0)
  }, [data])

  const orderStatusData = useMemo(() => {
    if (!data) return []
    const labels = { new: 'Nouvelles', in_progress: 'En cours', completed: 'Terminees', cancelled: 'Annulees' }
    return Object.entries(data.orders.by_status || {}).map(([k, v]) => ({
      name: labels[k] || k,
      value: v,
    }))
  }, [data])

  const modelData = useMemo(() => {
    if (!data) return []
    return Object.entries(data.openai.models_used || {}).map(([k, v]) => ({
      name: k,
      value: v,
    }))
  }, [data])

  const timeline = useMemo(() => {
    if (!data?.timeline) return []
    return data.timeline.slice(-60).map((row) => ({
      ...row,
      visitors: row.visitors ?? 0,
    }))
  }, [data])

  const visitorsMeta = data?.visitors || { unique_tracked: 0, visit_rows: 0 }

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <main className="pt-24 pb-16 max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-center py-32">
            <div className="spinner" />
          </div>
        </main>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <main className="pt-24 pb-16 max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-center text-[#f5f5f7]/50 py-32">Impossible de charger les KPI.</p>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-black">
      <Header />
      <main className="pt-24 pb-16 max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#f5f5f7] font-heading">
              KPI & Analytics
            </h1>
            <p className="text-base text-[#f5f5f7]/50 mt-1">
              Vue d'ensemble de l'activite
            </p>
          </div>
          <button
            onClick={loadKpi}
            className="flex items-center gap-2 text-sm font-medium text-[#f5f5f7]/50 hover:text-[#f5f5f7] px-4 py-2 border border-white/[0.08] hover:border-white/20 rounded-xl transition-all duration-[180ms]"
          >
            <RefreshCw size={14} />
            Actualiser
          </button>
        </div>

        {/* KPI cards row 1: Users + traffic */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
          <KpiCard
            icon={Globe}
            label="Visiteurs (suivi)"
            value={formatNumber(visitorsMeta.unique_tracked)}
            sub={`${formatNumber(visitorsMeta.visit_rows)} enregistrements (max 1 / jour / navigateur)`}
          />
          <KpiCard icon={Users} label="Utilisateurs" value={formatNumber(data.users.total)} sub={`${data.users.google} Google · ${data.users.email} email`} />
          <KpiCard icon={FileSearch} label="Analyses totales" value={formatNumber(data.analyses.total)} sub={`${data.analyses.anonymous} anonymes · ${data.analyses.authenticated} connectes`} />
          <KpiCard icon={TrendingUp} label="Avec economies" value={formatNumber(data.analyses.with_savings)} sub={`${data.analyses.total > 0 ? Math.round(data.analyses.with_savings / data.analyses.total * 100) : 0}% des analyses`} color="text-green-400" />
          <KpiCard icon={ShoppingCart} label="Commandes" value={formatNumber(data.orders.total)} sub={`Taux conversion: ${data.financial.conversion_rate}%`} />
        </div>

        {/* KPI cards row 2: Financial */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <KpiCard icon={DollarSign} label="Economies detectees" value={formatCurrency(data.financial.total_savings)} color="text-green-400" />
          <KpiCard icon={DollarSign} label="Commissions" value={formatCurrency(data.financial.total_commission)} />
          <KpiCard icon={DollarSign} label="Revenu (commandes)" value={formatCurrency(data.financial.revenue)} color="text-green-400" />
          <KpiCard icon={Cpu} label="Appels OpenAI" value={formatNumber(data.openai.total_calls)} sub={`${data.openai.failures} echecs · ${formatNumber(data.openai.total_tokens)} tokens`} />
        </div>

        {/* Timeline chart */}
        <ChartCard title="Activite dans le temps" className="mb-6">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={timeline}>
                <defs>
                  <linearGradient id="gradSiteVisitors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradAnalyses" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradOrders" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: 'rgba(245,245,247,0.3)' }}
                  tickLine={false}
                  axisLine={{ stroke: 'rgba(255,255,255,0.06)' }}
                  tickFormatter={(d) => d.slice(5)}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'rgba(245,245,247,0.3)' }}
                  tickLine={false}
                  axisLine={false}
                  width={40}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 12, color: 'rgba(245,245,247,0.5)' }}
                />
                <Area type="monotone" dataKey="visitors" name="Visiteurs site" stroke="#06b6d4" fill="url(#gradSiteVisitors)" strokeWidth={2} />
                <Area type="monotone" dataKey="analyses" name="Analyses" stroke="#22c55e" fill="url(#gradAnalyses)" strokeWidth={2} />
                <Area type="monotone" dataKey="users" name="Inscriptions" stroke="#3b82f6" fill="url(#gradUsers)" strokeWidth={2} />
                <Area type="monotone" dataKey="orders" name="Commandes" stroke="#f59e0b" fill="url(#gradOrders)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        {/* Analyses breakdown + bar */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <ChartCard title="Analyses: anonymes vs connectes">
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: 'rgba(245,245,247,0.3)' }}
                    tickLine={false}
                    axisLine={{ stroke: 'rgba(255,255,255,0.06)' }}
                    tickFormatter={(d) => d.slice(5)}
                  />
                  <YAxis tick={{ fontSize: 10, fill: 'rgba(245,245,247,0.3)' }} tickLine={false} axisLine={false} width={30} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11, color: 'rgba(245,245,247,0.5)' }} />
                  <Bar dataKey="anonymous" name="Anonymes" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="authenticated" name="Connectes" fill="#22c55e" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard title="Repartition utilisateurs">
            <div className="h-64 flex items-center justify-center">
              {userPieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={userPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={4}
                      dataKey="value"
                      stroke="none"
                    >
                      {userPieData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: '#2c2c2e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12 }}
                      itemStyle={{ color: '#f5f5f7' }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12, color: 'rgba(245,245,247,0.5)' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-[#f5f5f7]/30">Aucune donnee</p>
              )}
            </div>
          </ChartCard>
        </div>

        {/* Orders + OpenAI pie */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <ChartCard title="Commandes par statut">
            <div className="h-64 flex items-center justify-center">
              {orderStatusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={orderStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={4}
                      dataKey="value"
                      stroke="none"
                    >
                      {orderStatusData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: '#2c2c2e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12 }}
                      itemStyle={{ color: '#f5f5f7' }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12, color: 'rgba(245,245,247,0.5)' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-[#f5f5f7]/30">Aucune commande</p>
              )}
            </div>
          </ChartCard>

          <ChartCard title="Modeles OpenAI utilises">
            <div className="h-64 flex items-center justify-center">
              {modelData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={modelData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={4}
                      dataKey="value"
                      stroke="none"
                    >
                      {modelData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{ background: '#2c2c2e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12 }}
                      itemStyle={{ color: '#f5f5f7' }}
                    />
                    <Legend wrapperStyle={{ fontSize: 12, color: 'rgba(245,245,247,0.5)' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-[#f5f5f7]/30">Aucun appel</p>
              )}
            </div>
          </ChartCard>
        </div>

        {/* Engagement metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <KpiCard icon={ThumbsUp} label="Feedback positif" value={formatNumber(data.analyses.feedback_ok)} color="text-green-400" />
          <KpiCard icon={ThumbsDown} label="Feedback negatif" value={formatNumber(data.analyses.feedback_ko)} color="text-red-400" />
          <KpiCard icon={RefreshCw} label="Re-analyses" value={formatNumber(data.analyses.reanalyze_total)} />
          <KpiCard icon={Zap} label="Duree moy. OpenAI" value={`${formatNumber(data.openai.avg_duration_ms)}ms`} />
          <KpiCard icon={BarChart3} label="Taux claim" value={`${data.financial.claim_rate}%`} sub="Anonyme → connecte" />
        </div>

        {/* OpenAI calls timeline */}
        <ChartCard title="Appels OpenAI dans le temps" className="mb-6">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: 'rgba(245,245,247,0.3)' }}
                  tickLine={false}
                  axisLine={{ stroke: 'rgba(255,255,255,0.06)' }}
                  tickFormatter={(d) => d.slice(5)}
                />
                <YAxis tick={{ fontSize: 10, fill: 'rgba(245,245,247,0.3)' }} tickLine={false} axisLine={false} width={30} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="openai_calls" name="Appels OpenAI" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ChartCard>

        {/* Payment status breakdown */}
        <ChartCard title="Statuts de paiement" className="mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(data.payment_statuses || {}).map(([status, count]) => {
              const colorMap = {
                none: 'text-[#f5f5f7]/40',
                accepted: 'text-blue-400',
                in_progress: 'text-yellow-400',
                completed: 'text-green-400',
                cancelled: 'text-red-400',
                paid: 'text-green-400',
              }
              const labelMap = {
                none: 'Aucun',
                accepted: 'Accepte',
                in_progress: 'En cours',
                completed: 'Termine',
                cancelled: 'Annule',
                paid: 'Paye',
              }
              return (
                <div key={status} className="bg-black/30 rounded-xl px-4 py-3 border border-white/[0.04]">
                  <p className="text-xs uppercase tracking-[0.1em] font-semibold text-[#f5f5f7]/30 mb-1">
                    {labelMap[status] || status}
                  </p>
                  <p className={`text-xl font-black font-heading ${colorMap[status] || 'text-[#f5f5f7]'}`}>
                    {formatNumber(count)}
                  </p>
                </div>
              )
            })}
          </div>
        </ChartCard>
      </main>
    </div>
  )
}
